<template>
  <section class="reviews-page">
    <header class="reviews-header">
      <div>
        <p class="eyebrow">
          PRODUCT REVIEWS
        </p><h1>客户评价审核</h1><p>评价只能由已完成订单的购买用户提交；后台仅可查看并隐藏/恢复。</p>
      </div>
      <select
        v-model="selectedProductId"
        :disabled="loadingProducts"
        aria-label="选择商品"
      >
        <option value="">
          选择商品
        </option><option
          v-for="product in products"
          :key="product.id"
          :value="product.id"
        >
          {{ product.productNo }} · {{ product.name }}
        </option>
      </select>
    </header>
    <p
      v-if="errorMessage"
      class="reviews-alert reviews-alert--error"
    >
      {{ errorMessage }}
    </p><p
      v-if="successMessage"
      class="reviews-alert reviews-alert--success"
    >
      {{ successMessage }}
    </p>
    <div
      v-if="selectedProductId"
      class="reviews-list-card"
    >
      <div class="reviews-card-heading">
        <div><h2>评价记录</h2><p>不提供后台创建、编辑文本或上传媒体入口。</p></div><button
          class="reviews-button reviews-button--quiet"
          type="button"
          :disabled="loadingRows"
          @click="loadRows"
        >
          刷新
        </button>
      </div>
      <p
        v-if="loadingRows"
        class="reviews-empty"
      >
        正在加载…
      </p><p
        v-else-if="rows.length === 0"
        class="reviews-empty"
      >
        当前商品暂无用户评价。
      </p>
      <article
        v-for="row in rows"
        v-else
        :key="row.id"
        class="reviews-row"
      >
        <div class="reviews-row-media">
          <img
            v-for="media in activeMedia(row)"
            :key="media.id"
            :src="media.url"
            :alt="`${row.reviewerLabel}评价图片`"
          >
        </div>
        <div class="reviews-row-body">
          <div class="reviews-row-title">
            <strong>{{ row.reviewerLabel }}</strong><span>{{ row.specificationText || "规格快照未提供" }}</span>
          </div><p>{{ row.reviewText || "未填写文字评价" }}</p><small>订单项 {{ row.orderItemId }} · {{ row.status === "ACTIVE" ? "公开" : "已隐藏" }} · {{ row.createdAt }}</small>
        </div>
        <button
          class="reviews-button reviews-button--quiet"
          type="button"
          :disabled="busy"
          @click="toggleStatus(row)"
        >
          {{ row.status === "ACTIVE" ? "隐藏" : "恢复公开" }}
        </button>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { listProducts, type ProductSummary } from "../api/products";
import { listProductReviews, setReviewStatus, type ProductReview } from "../api/reviews";
const products = ref<ProductSummary[]>([]); const selectedProductId = ref(""); const rows = ref<ProductReview[]>([]); const loadingProducts = ref(true); const loadingRows = ref(false); const busy = ref(false); const errorMessage = ref(""); const successMessage = ref("");
function activeMedia(row: ProductReview) { return row.media.filter((media) => media.status === "ACTIVE"); }
async function loadRows() { if (!selectedProductId.value) return; loadingRows.value = true; errorMessage.value = ""; try { rows.value = await listProductReviews(selectedProductId.value); } catch (error) { errorMessage.value = error instanceof Error ? error.message : "评价加载失败"; } finally { loadingRows.value = false; } }
async function toggleStatus(row: ProductReview) { busy.value = true; errorMessage.value = ""; successMessage.value = ""; try { await setReviewStatus(row.id, row.status === "ACTIVE" ? "HIDDEN" : "ACTIVE"); successMessage.value = row.status === "ACTIVE" ? "评价已隐藏" : "评价已恢复公开"; await loadRows(); } catch (error) { errorMessage.value = error instanceof Error ? error.message : "评价状态更新失败"; } finally { busy.value = false; } }
watch(selectedProductId, () => { void loadRows(); });
onMounted(async () => { try { products.value = await listProducts(); selectedProductId.value = products.value[0]?.id ?? ""; } catch (error) { errorMessage.value = error instanceof Error ? error.message : "商品加载失败"; } finally { loadingProducts.value = false; } });
</script>

<style scoped>
.reviews-page { width: min(100% - 56px, 1180px); margin: 0 auto; padding: 36px 0 80px; } .reviews-header, .reviews-card-heading, .reviews-row, .reviews-row-title { display: flex; align-items: center; justify-content: space-between; gap: 16px; } .reviews-header { margin-bottom: 24px; } .reviews-header h1, .reviews-list-card h2 { margin: 0; } .reviews-header p, .reviews-card-heading p { color: var(--admin-muted); margin: 8px 0 0; } .eyebrow { color: var(--admin-primary-deep); font-size: 12px; letter-spacing: .12em; } .reviews-header select { min-width: 280px; border: 1px solid var(--admin-outline); border-radius: 8px; padding: 10px 12px; background: #fff; } .reviews-list-card { padding: 24px; margin-top: 20px; background: #fff; border: 1px solid var(--admin-outline-soft); border-radius: 12px; } .reviews-button { border: 0; border-radius: 8px; padding: 10px 16px; background: var(--admin-primary); color: white; font-weight: 700; } .reviews-button--quiet { background: var(--admin-primary-soft); color: var(--admin-primary-deep); } .reviews-alert { padding: 12px 16px; border-radius: 8px; } .reviews-alert--error { color: #9a2c2c; background: #fff0f0; } .reviews-alert--success { color: #217849; background: #effaf2; } .reviews-empty { color: var(--admin-muted); padding: 24px 0; } .reviews-row { align-items: flex-start; padding: 18px 0; border-top: 1px solid var(--admin-outline-soft); } .reviews-row-media { display: flex; gap: 8px; width: 180px; flex-wrap: wrap; } .reviews-row-media img { width: 54px; height: 54px; object-fit: cover; border-radius: 6px; background: var(--admin-surface-low); } .reviews-row-body { flex: 1; min-width: 0; } .reviews-row-title { justify-content: flex-start; } .reviews-row-title span, .reviews-row-body small { color: var(--admin-muted); } .reviews-row-body p { margin: 8px 0; color: var(--admin-ink); white-space: pre-wrap; } @media (max-width: 760px) { .reviews-page { width: min(100% - 28px, 640px); } .reviews-header, .reviews-row { align-items: stretch; flex-direction: column; } .reviews-header select { min-width: 0; width: 100%; } }
</style>
